import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { observer } from 'mobx-react';
import { compose } from 'recompose';

import { SaveButton } from '../../../../../../components/Button/ActionButtons';
import { StyledTextField } from '../../../../../../components/Inputs';
import LoadingOverlay from '../../../../../components/LoadingOverlay';
import RadioButtonGroup from '../../../../../components/RadioButtonGroup';
import InstagramUser, { accountStatus, instagramPermissions } from '../../../../../../Model/InstagramUser';
import UserContext from '../../../../../../context/UserContext';
import FirebaseContext from '../../../../../../context/FirebaseContext';
import ExternalCoachContext from '../../../../../context/ExternalCoachContext';
import useToast from '../../../../../hooks/useToast';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';
import {
  Card,
  HeaderContainer,
  SubTitle,
  Title,
  TitleContainer,
  Container,
} from '../../../../../../components/v2/Card';

import { SettingOption } from './utils';
import {
  ButtonContainer,
  ActionLabel,
  SettingContainer,
} from './styles';
import texts from './texts.json';

const TextSetterMessaging = ({ instaUser }) => {
  const {
    accountStatus: connectedStatus,
    grantedPermissions,
    isTextSetterEnabled: dbTextSetterEnabled,
  } = instaUser;
  const [isLoading, setIsLoading] = useState(false);
  const [webPageURL, setWebPageURL] = useState('');
  const [textSetterEnabled, setTextSetterEnabled] = useState(dbTextSetterEnabled);
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { name } = useContext(UserContext);
  const { coachDoc } = useContext(ExternalCoachContext);
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  // Initialize webPageURL from coachDoc
  useEffect(() => {
    if (coachDoc?.webPageUrl && isComponentMountedRef.current) {
      setWebPageURL(coachDoc.webPageUrl);
    }
  }, [coachDoc, isComponentMountedRef]);

  const handleSave = useCallback(async () => {
    try {
      setIsLoading(true);

      // Validate text setter settings
      if (textSetterEnabled) {
        if (connectedStatus !== accountStatus.AUTHORIZED) {
          throw new Error(texts.validationErrors.accountNotAuthorized);
        }

        const hasMessagePermissions = grantedPermissions.includes(
          instagramPermissions.INSTAGRAM_BUSINESS_MANAGE_MESSAGES,
        );

        if (!hasMessagePermissions) {
          throw new Error(texts.validationErrors.missingMessagePermissions);
        }

        if (!webPageURL) {
          throw new Error(texts.validationErrors.leadFormUrlNotSet);
        }
      }

      // Save coach website URL updates if web page URL is changed
      if (webPageURL !== coachDoc.webPageUrl) {
        await coachDoc.setWebPageUrl(webPageURL);
      }

      if (textSetterEnabled !== dbTextSetterEnabled) {
        const response = await remote(
          'instagram-appSubscription',
          {
            instagramUserId: instaUser.id,
            subscribe: textSetterEnabled,
          },
          { method: 'POST' },
        );
        const json = await response.json();
        if (!json.success) throw new Error(json.message);
        await instaUser.toggleTextSetter(textSetterEnabled, name);
      }

      showToast(texts.messageSettingSaveSuccessful);
    } catch (err) {
      if (isComponentMountedRef.current) {
        setTextSetterEnabled(dbTextSetterEnabled);
        setWebPageURL(coachDoc.webPageUrl);
      }
      showToast(format(texts.messageSettingSaveFailed, { error: err.message }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    instaUser,
    remote,
    textSetterEnabled,
    showToast,
    name,
    coachDoc,
    webPageURL,
    connectedStatus,
    grantedPermissions,
    dbTextSetterEnabled,
    isComponentMountedRef,
  ]);

  return (
    <Card>
      <HeaderContainer>
        <TitleContainer>
          <Title>{texts.textSetter}</Title>
          <SubTitle>{texts.textSetterDescription}</SubTitle>
        </TitleContainer>
      </HeaderContainer>
      <Container>
        <RadioButtonGroup
          options={[
            {
              label: texts.enabled,
              value: SettingOption.ENABLED,
            },
            {
              label: texts.disabled,
              value: SettingOption.DISABLED,
            },
          ]}
          selectedOption={textSetterEnabled ? SettingOption.ENABLED : SettingOption.DISABLED}
          onOptionChange={(value) => setTextSetterEnabled(value === SettingOption.ENABLED)}
        />
      </Container>

      <SettingContainer>
        <ActionLabel>{texts.coachSiteURL}</ActionLabel>
        <StyledTextField
          value={webPageURL}
          onChange={(event) => setWebPageURL(event.target.value)}
          placeholder={texts.coachSiteURLPlaceholder}
        />
      </SettingContainer>

      <ButtonContainer>
        <SaveButton
          onClick={handleSave}
        >
          {texts.save}
        </SaveButton>
      </ButtonContainer>
      <LoadingOverlay isLoading={isLoading} />
    </Card>
  );
};

TextSetterMessaging.propTypes = {
  instaUser: PropTypes.instanceOf(InstagramUser),
};

TextSetterMessaging.defaultProps = {
  instaUser: {
    accountStatus: accountStatus.UNAUTHORIZED,
    grantedPermissions: [],
    isTextSetterEnabled: false,
  },
};

export default compose(
  observer,
)(TextSetterMessaging);
